import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Tree } from 'primereact/tree';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog } from 'primereact/confirmdialog';

import GraphQLWrapper from '../UI/GraphQLWrapper/GraphQLWrapper';

import CustomProgressBar from './CustomProgressBar/CustomProgressBar';
import ElapsedTime from './CustomProgressBar/ElapsedTIme';
import TextName from './TextName/TextName';
import TestResultsSpinner from '../UI/Spinner/TestResultsSpinner';
import {
  CANCELLED,
  // FINISHED,
  // TEST_RESULTS_REQUEST_LIMIT,
  // REGIONS_DICTIONARY,
  // ATT_ENV,
  PASSED,
  FAILED,
  NOT_EXECUTED,
} from '../../Constants/constants';
import { isEmpty } from '../../Utils/helpers';
import { getDate, getTime } from '../../Utils/helpers';

import './TestResults.css';

import ErrorFallback from '../Error/ErrorFallback';
import { AppContext } from '../UI/contexts';
import Filters from './Filters/Filters';
import {
  setCurrentTestResults,
  setOriginalTestResults,
} from '../../App/Slices/TestResults';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';

import { useTestExecution } from './Hooks/useTestExecution';
import { useTestPolling } from './Hooks/useTestPolling';
import { useScreenshotModal } from './Hooks/useScreenshotModal';
import { useTreeView } from './Hooks/useTreeView';
import { useTestResultsQueries } from './Hooks/useTestResultsQueries';
import useCancelTestExecution from './Hooks/useCancelTestExecution';

// Show a test result given a valid Id
function TestResults({
  id,
  queryGetResultsById,
  queryGetDuration,
  queryGetRegionAndName,
  queryGetUrlTestsIds,
  queryGetFailedExecutions,
  onCancelRedirectUrl,
  cancelMutation,
  getTestResultsQueryName,
  getNameAndRegionQueryName,
  getDurationQueryName,
  dsProviderId,
  filterConfigs,
  getTestResultsQueryDataName,
  typeName,
}) {
  const dispatch = useDispatch();
  const { user } = useContext(AppContext);
  const testResults = useSelector(
    (state) => state.testResults.currentTestResults
  );
  const originalTestResults = useSelector(
    (state) => state.testResults.originalTestResults
  );

  const {
    status: testStatus,
    userOwnsTest,
    consecutiveFails,
    exists: testExecutionExists,
  } = useSelector((state) => state.testResults.execution);

  // Customs hooks
  const {
    seconds,
    setSeconds,
    maxDuration,
    start,
    queryFinished,
    pollInterval,
    urlsLength,
    name,
    region,
    stopCount,
    setStopCount,
  } = useTestExecution();

  const { pendingUrlTests } = useTestResultsQueries({
    id,
    user,
    queryGetDuration,
    queryGetRegionAndName,
    queryGetUrlTestsIds,
    queryGetFailedExecutions,
    getDurationQueryName,
    getNameAndRegionQueryName,
    getTestResultsQueryName,
    onCancelRedirectUrl,
    queryFinished,
    start,
    urlsLength,
    maxDuration,
    pollInterval,
    setStopCount,
    name,
    region,
  });

  const {
    visible: screenshotDialogVisible,
    src: screenshotSrc,
    openModal: openScreenshotModal,
    closeModal: closeScreenshotModal,
  } = useScreenshotModal();

  const {
    expandedKeys,
    setExpandedKeys,
    collapseAll,
    expandedFailed,
    nodeTemplate,
  } = useTreeView({
    testResults,
    openScreenshotModal,
    closeScreenshotModal,
  });

  const { allTestArrived, pollingError } = useTestPolling({
    queryGetResultsById,
    id,
    getTestResultsQueryName,
    getTestResultsQueryDataName,
    typeName,
    pendingUrlTestsRef: pendingUrlTests,
    pollIntervalRef: pollInterval,
    stopCount,
    setStopCount,
    onUpdateResults: (newResults) => {
      const updatedResults = [
        ...(testResults || []),
        ...newResults.filter(
          (r) => !testResults?.map((tr) => tr.text).includes(r.text)
        ),
      ];

      pendingUrlTests.current = pendingUrlTests.current.filter(
        (pending) => !newResults.some((r) => r.url === pending.url)
      );

      dispatch(setCurrentTestResults(updatedResults));

      if (pendingUrlTests.current.length === 0) {
        dispatch(setOriginalTestResults(updatedResults));
      }
    },
  });

  const { cancelling, cancel } = useCancelTestExecution(cancelMutation);

  const treeView = useRef();

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (stopCount) return;
    setTimeout(() => {
      setSeconds((Date.now() - start.current) / 1000);
    }, 1000);
  });

  if (queryFinished.current && !testExecutionExists) {
    return (
      <ErrorFallback error={{ message: 'Test execution does not exist' }} />
    );
  }

  const getUrlTestResultsByStatus = (status, pTestResults) => {
    return (pTestResults || []).filter((tr) => tr.status === status);
  };

  // Toggle node expand/collapse when clicking the node body (avoids clicking toggler or interactive elements)
  function handleTreeNodeClick(event) {
    const node = event.node;
    const original = event.originalEvent;

    if (!node) return;

    if (original && original.target && original.target.closest) {
      const ignore = original.target.closest(
        '.p-tree-toggler, .p-tree-toggler-icon, button, a, input, textarea, .no-node-toggle'
      );
      if (ignore) return;
    }

    // If it's a leaf node, don't try to expand/collapse
    if (node.leaf) return;

    const key = node.key;
    setExpandedKeys((prev) => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = true;
      }
      return next;
    });
  }
  const treeViewPanel = isEmpty(testResults) ? (
    <div
      className='d-flex justify-content-center mt-2'
      style={{ color: 'gray' }}
    >
      No results found. Please check your filters.
    </div>
  ) : (
    <div className='accordion-container'>
      <Tree
        ref={treeView}
        value={testResults}
        selectionMode='single'
        selectionKeys={expandedKeys}
        onToggle={(e) => {
          setExpandedKeys(e.value);
        }}
        expandedKeys={expandedKeys}
        onNodeClick={handleTreeNodeClick}
        nodeTemplate={nodeTemplate}
        className='w-full'
      />
    </div>
  );

  const label =
    queryFinished.current && ((stopCount && 'Duration') || 'Elapsed Time');

  const showSeconds =
    (queryFinished.current &&
      ((stopCount && maxDuration.current) || seconds)) ||
    null;

  const dateAndTime =
    start.current !== null
      ? `${getDate(start.current)}-${getTime(start.current)}`
      : '';

  const showCancelButton = queryFinished.current && label !== 'Duration';

  const filtersPanel = showFilters && (
    <div className='d-flex justify-content-center'>
      <div className='filters-panel'>
        <div className='e-card'>
          <div className='e-card-content'>
            <Filters setShowFilters={setShowFilters} />
          </div>
        </div>
      </div>
    </div>
  );

  const view = (
    <div id='test-results-content'>
      <div className='d-flex flex-column align-items-center'>
        {showCancelButton && (
          <div
            className='d-flex justify-content-center'
            style={{ width: '100%' }}
          >
            <div
              className='d-flex justify-content-end'
              style={{ width: '100%' }}
            >
              {userOwnsTest && (
                <Button
                  className='p-button-primary'
                  onClick={() => {
                    cancel(id, onCancelRedirectUrl);
                  }}
                  disabled={cancelling}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Test Execution'}
                </Button>
              )}
            </div>
          </div>
        )}
        <div className='row justify-content-center' style={{ width: '100%' }}>
          <ElapsedTime className='col-sm' seconds={showSeconds} label={label} />
        </div>
        <div className='row justify-content-center pb-4'>
          <TextName className='col-sm' label='Name:' text={name.current} />
          {region.current !== null && (
            <TextName
              className='col-sm'
              label='Region:'
              text={region.current}
            />
          )}
          <TextName className='col-sm' label='Start Date:' text={dateAndTime} />
        </div>
        <CustomProgressBar
          passed={getUrlTestResultsByStatus(PASSED, originalTestResults).length}
          failed={getUrlTestResultsByStatus(FAILED, originalTestResults).length}
          notExecuted={
            getUrlTestResultsByStatus(NOT_EXECUTED, originalTestResults).length
          }
          total={urlsLength.current}
          consecutiveFails={consecutiveFails}
          current={originalTestResults ? originalTestResults.length : 0}
        />
      </div>

      {testResults && allTestArrived ? (
        <>
          {allTestArrived && (
            <div className='d-flex justify-content-center'>
              <div className='test-results-actions-panel'>
                <Button
                  icon='pi pi-filter'
                  onClick={() => {
                    setShowFilters(!showFilters);
                  }}
                  className='p-button-text'
                >
                  Filters
                </Button>
                <Button icon='pi pi-chevron-down' onClick={expandedFailed}>
                  Expand Failed
                </Button>
                <Button icon='pi pi-chevron-up' onClick={collapseAll}>
                  Collapse All
                </Button>
              </div>
            </div>
          )}

          {filtersPanel}

          {testStatus === CANCELLED && (
            <div className='d-flex justify-content-center'>
              <div
                style={{ color: '#124069', fontStyle: 'italic', width: '85%' }}
              >
                Test Execution was cancelled by user
              </div>
            </div>
          )}

          {treeViewPanel}

          <Dialog
            visible={screenshotDialogVisible}
            onHide={closeScreenshotModal}
            modal
            style={{ width: '90vw' }}
            position='center'
            draggable={false}
            resizable={false}
            closeOnEscape
            maximizable
          >
            <img
              src={screenshotSrc}
              alt='Screenshot'
              style={{ width: '100%' }}
            />
          </Dialog>

          <ConfirmDialog />
        </>
      ) : (
        <div>
          <div className='in-progress-te-message'>
            {cancelling
              ? 'Cancelling test execution...'
              : 'Tests in progress. Please wait...'}
          </div>
          <TestResultsSpinner />
        </div>
      )}
    </div>
  );

  return (
    <GraphQLWrapper
      loading={!queryFinished.current}
      error={pollingError}
      component={view}
    />
  );
}

export default TestResults;
